// کلاینت تایپ‌شده Phoenix (openapi-fetch) با Middlewareهای توکن، زبان، Idempotency و خطا (AP-008).
import createClient, { type Client, type Middleware } from 'openapi-fetch';
import { ApiError, ErrorCodes, networkError, problemToApiError } from './errors';
import type { PhoenixPaths } from './paths';

export type TokenKind = 'keycloak' | 'tenant';

export interface ApiEvents {
  /** 423 (PHX-PLT-0423): مستأجر ReadOnly/Locked — پوسته بنر حالت دسترسی را نشان می‌دهد (AC-PLT-004.2). */
  onLocked?: (error: ApiError) => void;
  /** 403 PHX-PLT-0403: Feature غیرفعال — پوسته پیشنهاد ارتقا را نشان می‌دهد (AC-PLT-031.2). */
  onFeatureDisabled?: (error: ApiError) => void;
  /** هر خطای دیگر (برای لاگ یا Snackbar عمومی). */
  onError?: (error: ApiError) => void;
}

export interface PhoenixClientOptions extends ApiEvents {
  /** نشانی پایه؛ خالی یعنی همان Origin (Proxy توسعه Vite یا Ingress). */
  baseUrl?: string;
  /** توکن جاری (Keycloak یا توکن مستأجر بسته به کلاینت). `forceRefresh` پس از 401 یک‌بار فراخوانده می‌شود. */
  getToken: (options?: { forceRefresh?: boolean }) => Promise<string | undefined>;
  /** زبان جاری UI برای Accept-Language (عنوان ProblemDetails). */
  getLanguage?: () => string;
  /**
   * 401: اگر true برگرداند، درخواست یک‌بار با توکن تازه تکرار می‌شود (تمدید توکن مستأجر یا Keycloak)؛
   * در غیر این صورت پوسته کاربر را به ورود یا انتخاب مستأجر می‌برد.
   */
  onUnauthorized?: (error: ApiError) => Promise<boolean>;
  /** پیاده‌سازی fetch (تست). */
  fetch?: typeof globalThis.fetch;
  /** سازنده کلید Idempotency (تست). */
  newIdempotencyKey?: () => string;
}

export type PhoenixClient = Client<PhoenixPaths>;

const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/**
 * Middleware سرآیندها: Accept-Language و Idempotency-Key. قرارداد OpenAPI فعلاً POSTهای نیازمند کلید را علامت نمی‌زند،
 * پس همه POSTها کلید UUID تازه می‌گیرند مگر فراخوان کلید خود را بدهد (برای تکرار امن همان فرم — C-AGENT-133).
 */
export function headersMiddleware(
  options: Pick<PhoenixClientOptions, 'getLanguage' | 'newIdempotencyKey'>,
): Middleware {
  return {
    onRequest({ request }) {
      const language = options.getLanguage?.();
      if (language && !request.headers.has('Accept-Language')) {
        request.headers.set('Accept-Language', language);
      }
      if (request.method === 'POST' && !request.headers.has(IDEMPOTENCY_HEADER)) {
        request.headers.set(IDEMPOTENCY_HEADER, (options.newIdempotencyKey ?? (() => crypto.randomUUID()))());
      }
      return request;
    },
  };
}

async function readProblem(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.clone().text();
  } catch {
    body = undefined;
  }
  return problemToApiError(response.status, body);
}

/**
 * fetch با توکن، تکرار یک‌باره پس از 401 و انتشار رویدادهای 423 و 403 Feature. توکن فقط در حافظه (پارامتر getToken) است.
 */
export function createAuthorizedFetch(options: PhoenixClientOptions): (input: Request) => Promise<Response> {
  const baseFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  async function send(request: Request, forceRefresh: boolean): Promise<Response> {
    const token = await options.getToken(forceRefresh ? { forceRefresh: true } : undefined);
    const attempt = request.clone();
    if (token) {
      attempt.headers.set('Authorization', `Bearer ${token}`);
    }
    try {
      return await baseFetch(attempt);
    } catch (cause) {
      throw networkError(cause);
    }
  }

  return async (request: Request) => {
    let response = await send(request, false);

    if (response.status === 401 && options.onUnauthorized) {
      const retry = await options.onUnauthorized(await readProblem(response));
      if (retry) {
        response = await send(request, true);
      }
    }

    if (!response.ok) {
      const error = await readProblem(response);
      if (error.traceId) {
        lastErrorTraceId = error.traceId;
      }
      if (response.status === 423 || error.code === ErrorCodes.tenantReadOnly) {
        options.onLocked?.(error);
      } else if (error.code === ErrorCodes.featureDisabled) {
        options.onFeatureDisabled?.(error);
      } else {
        options.onError?.(error);
      }
    }

    return response;
  };
}

let lastErrorTraceId: string | undefined;

/**
 * شناسه همبستگی (traceId) آخرین پاسخ ناموفق API در این زبانه — برای پیوست خودکار به تیکت پشتیبانی Phoenix
 * (AC-CHN-045.1؛ فقط شناسه فنی، نه متن یا داده خطا).
 */
export function getLastErrorTraceId(): string | undefined {
  return lastErrorTraceId;
}

/** پاک کردن شناسه آخرین خطا (تست). */
export function resetLastErrorTraceId(): void {
  lastErrorTraceId = undefined;
}

/** ساخت کلاینت تایپ‌شده روی همه مسیرهای OpenAPI ماژول‌ها. */
export function createPhoenixClient(options: PhoenixClientOptions): PhoenixClient {
  const client = createClient<PhoenixPaths>({
    baseUrl: options.baseUrl ?? '',
    fetch: createAuthorizedFetch(options),
  });
  client.use(headersMiddleware(options));
  return client;
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * نتیجه openapi-fetch را باز می‌کند: داده موفق برمی‌گردد و خطا به ApiError تبدیل و پرتاب می‌شود (برای TanStack Query).
 */
export async function unwrap<T>(promise: Promise<FetchResult<T>>): Promise<T> {
  const result = await promise;
  if (result.response.ok && result.error === undefined) {
    return result.data as T;
  }
  throw result.error instanceof ApiError
    ? result.error
    : problemToApiError(result.response.status, result.error);
}
